import { Inject, Injectable } from '@angular/core';
import { first } from 'rxjs/operators';
import AWSAppSyncClient, { AUTH_TYPE } from 'aws-appsync';
import { AuthOptions } from 'aws-appsync-auth-link';
import { BehaviorSubject } from 'rxjs';
import { IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';

import introspectionQueryResultData from './fragmentTypes.json';
import { CORE_SERVICES_CONFIG, CoreServiceConfig } from '../../core-services.config';
import { AuthenticationService } from '../authentication/authentication.service';
import { isNotNullOrUndefined } from '../utils/observable.utils';
import { CoreServicesModule } from '../../core-services.module';

type GraphQlConfig = {
  appsync: {
      default: {
          url: string;
          region: string;
          desc: string;
      };
      list: {
          url: string;
          region: string;
          desc: string;
      }[];
  };
  clientGen: (opt: any) => AWSAppSyncClient<any>;
}

@Injectable({
  providedIn: CoreServicesModule,
})
export class GraphqlApiService {
  readonly awsConfig: GraphQlConfig;
  readonly client$ = new BehaviorSubject<AWSAppSyncClient<any> | null>(null);
  readonly envGateway$ = new BehaviorSubject<string | null>(null);

  constructor(
    @Inject(CORE_SERVICES_CONFIG) config: CoreServiceConfig,
    private readonly authenticationService: AuthenticationService,
  ) {
    this.awsConfig = config.aws;
    this.envGateway$.next(this.awsConfig.appsync.default.desc);
    this.buildByDefault();
  }

  /** the starting environment is always the same (DEV).
   * problems could arise;
   * - only operators can move from one environment to another, but to know
   * if you are an operator you must be connected to a particular environment. */
  buildByDefault(usePreference = false) {
    const region = localStorage.getItem('appsync.region') || undefined;
    const gateway = localStorage.getItem('appsync.gateway') || undefined;
    const desc = localStorage.getItem('appsync.desc') || undefined;
    if (region && gateway && desc && usePreference) {
      this.build(region, gateway, desc);
    } else {
      this.build();
    }
  }

  async build(
    region=this.awsConfig.appsync.default.region,
    gateway=this.awsConfig.appsync.default.url,
    desc=this.awsConfig.appsync.default.desc,
  ) {
    const auth: AuthOptions = {
      type: AUTH_TYPE.OPENID_CONNECT,
      jwtToken: async () => {
        const user = await this.authenticationService.user$.pipe(isNotNullOrUndefined(), first()).toPromise();
        return await user.getIdToken();
      },
    };

    const fragmentMatcher = new IntrospectionFragmentMatcher({
      introspectionQueryResultData,
    });

    const client = this.awsConfig.clientGen({
      auth,
      disableOffline: true,
      region,
      cacheOptions: {
        fragmentMatcher,
      },
      url: gateway,
    });

    await client.hydrated();
    this.client$.next(client);
    this.envGateway$.next(desc);
  }
}
